import { useEffect } from "react";
import { SSE, type SSEvent } from "sse.js";
import type {
  KioskPermissionDeniedContext,
  KioskSessionInvalidContext,
  KioskStreamFailureContext,
} from "./types";
import { getHttpStatus } from "./request-failure";

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const STALL_TIMEOUT_MS = 30 * 60_000;
const STALL_CHECK_INTERVAL_MS = 15_000;
const UNAVAILABLE_AFTER_FAILURES = 3;

type SseTelemetry = (
  event: string,
  properties?: Record<string, unknown>,
) => void;

type UseManualSseOptions = {
  url: string | undefined;
  zoneId: string;
  getAccessToken: () => string | undefined;
  refreshSession: () => Promise<void>;
  onScan: (event: MessageEvent) => void;
  onPermissionDenied: (context: KioskPermissionDeniedContext) => void;
  onSessionInvalid: (context: KioskSessionInvalidContext) => void;
  onUnavailable: (context: KioskStreamFailureContext) => void;
  onRecovered: () => void;
  capture: SseTelemetry;
};

/**
 * Owns the scan stream lifecycle without relying on sse.js reconnection.
 * Every attempt creates a new instance so its Authorization header always
 * contains the latest access token.
 */
export function useManualSse({
  url,
  zoneId,
  getAccessToken,
  refreshSession,
  onScan,
  onPermissionDenied,
  onSessionInvalid,
  onUnavailable,
  onRecovered,
  capture,
}: UseManualSseOptions) {
  useEffect(() => {
    if (!url || !zoneId || !getAccessToken()) {
      capture("sse_misconfigured", {
        zone_id: zoneId,
        has_url: Boolean(url),
        has_token: Boolean(getAccessToken()),
      });
      return;
    }
    const streamUrl = url;

    let source: SSE | null = null;
    let reconnectTimer: number | undefined;
    let generation = 0;
    let reconnectAttempt = 0;
    let consecutiveFailures = 0;
    let unavailableReported = false;
    let disposed = false;
    let permissionDenied = false;
    let sessionInvalid = false;
    let refreshing = false;
    let lastActivityAt = Date.now();
    let connectStartedAt = 0;

    const markActivity = () => {
      lastActivityAt = Date.now();
    };

    const cancelReconnect = () => {
      if (reconnectTimer !== undefined) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
    };

    const closeCurrent = () => {
      const current = source;
      source = null;
      current?.close();
    };

    const reconnectDelay = () => {
      const exponential = Math.min(
        INITIAL_RECONNECT_DELAY_MS * 2 ** reconnectAttempt,
        MAX_RECONNECT_DELAY_MS,
      );
      const jittered = exponential * (0.75 + Math.random() * 0.5);
      return Math.min(Math.round(jittered), MAX_RECONNECT_DELAY_MS);
    };

    const scheduleReconnect = (
      reason: string,
      immediate = false,
      status?: number,
    ) => {
      if (
        disposed ||
        permissionDenied ||
        sessionInvalid ||
        reconnectTimer !== undefined
      )
        return;

      closeCurrent();
      const delayMs = immediate ? 0 : reconnectDelay();
      reconnectAttempt += 1;

      if (!immediate) {
        consecutiveFailures += 1;
        if (
          consecutiveFailures >= UNAVAILABLE_AFTER_FAILURES &&
          !unavailableReported
        ) {
          unavailableReported = true;
          onUnavailable({
            operation: "scan_stream",
            retryCount: consecutiveFailures,
            status,
          });
        }
      }
      capture("sse_reconnect_scheduled", {
        zone_id: zoneId,
        reason,
        delay_ms: delayMs,
        retry_count: reconnectAttempt,
      });
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delayMs);
    };

    const denyPermission = () => {
      if (permissionDenied || disposed) return;
      permissionDenied = true;
      cancelReconnect();
      closeCurrent();
      capture("sse_permission_denied", {
        zone_id: zoneId,
        http_status: 403,
        severity: "critical",
      });
      onPermissionDenied({ operation: "scan_stream", status: 403 });
    };

    const invalidateSession = (error: unknown) => {
      if (sessionInvalid || disposed) return;
      sessionInvalid = true;
      cancelReconnect();
      closeCurrent();
      const status = getHttpStatus(error);
      const reason = getAccessToken()
        ? "refresh_rejected"
        : "credentials_missing";
      capture("sse_session_invalid", {
        zone_id: zoneId,
        http_status: status,
        reason,
        severity: "critical",
      });
      onSessionInvalid({ operation: "scan_stream", reason, status });
    };

    function connect() {
      if (disposed || permissionDenied || sessionInvalid) return;

      const token = getAccessToken();
      if (!token) {
        const error = new Error("Credentials are no longer available");
        error.name = "MissingRefreshTokenError";
        invalidateSession(error);
        return;
      }

      closeCurrent();
      const connectionGeneration = ++generation;
      connectStartedAt = Date.now();
      markActivity();
      capture("sse_connect_attempt", {
        zone_id: zoneId,
        retry_count: reconnectAttempt,
      });

      const nextSource = new SSE(streamUrl, {
        autoReconnect: false,
        useLastEventId: false,
        headers: { Authorization: `Bearer ${token}` },
      });
      source = nextSource;

      const isCurrent = () =>
        !disposed &&
        !permissionDenied &&
        !sessionInvalid &&
        generation === connectionGeneration &&
        source === nextSource;

      nextSource.addEventListener("scan", (event: SSEvent) => {
        if (!isCurrent()) return;
        markActivity();
        onScan(event as unknown as MessageEvent);
      });
      nextSource.addEventListener("message", () => {
        if (isCurrent()) markActivity();
      });
      nextSource.addEventListener("open", (event: SSEvent) => {
        if (!isCurrent()) return;
        const status = event.responseCode;
        if (status === undefined || status < 200 || status >= 300) {
          capture("sse_open_rejected", {
            zone_id: zoneId,
            response_code: status,
            retry_count: reconnectAttempt,
          });
          return;
        }
        markActivity();
        reconnectAttempt = 0;
        consecutiveFailures = 0;
        if (unavailableReported) {
          unavailableReported = false;
          onRecovered();
        }
        capture("sse_open", {
          zone_id: zoneId,
          ms_to_open: Date.now() - connectStartedAt,
        });
      });
      nextSource.addEventListener("readystatechange", () => {
        if (!isCurrent()) return;
        markActivity();
        if (nextSource.readyState === SSE.CLOSED) {
          capture("sse_closed", { zone_id: zoneId });
          scheduleReconnect("closed");
        }
      });
      nextSource.addEventListener("error", (rawEvent: SSEvent) => {
        if (!isCurrent()) return;
        markActivity();
        const event = rawEvent as SSEvent;
        const status = event.responseCode;

        if (status === 403) {
          denyPermission();
          return;
        }

        if (status === 401) {
          closeCurrent();
          if (refreshing) return;
          refreshing = true;
          capture("sse_token_refresh", {
            zone_id: zoneId,
            outcome: "started",
          });
          void refreshSession()
            .then(() => {
              if (disposed || permissionDenied || sessionInvalid) return;
              capture("sse_token_refresh", {
                zone_id: zoneId,
                outcome: "success",
              });
              reconnectAttempt = 0;
              connect();
            })
            .catch((error: unknown) => {
              if (disposed || permissionDenied || sessionInvalid) return;
              const status = getHttpStatus(error);
              capture("sse_token_refresh", {
                zone_id: zoneId,
                outcome: "failed",
              });
              capture("sse_refresh_failed", {
                zone_id: zoneId,
                error_name:
                  error instanceof Error ? error.name : "UnknownError",
              });
              const transient =
                status === undefined ||
                status === 408 ||
                status === 429 ||
                status >= 500;
              if (
                !transient ||
                !getAccessToken() ||
                (error instanceof Error &&
                  error.name === "MissingRefreshTokenError")
              ) {
                invalidateSession(error);
              } else {
                scheduleReconnect("refresh_failed");
              }
            })
            .finally(() => {
              refreshing = false;
            });
          return;
        }

        capture("sse_error", {
          zone_id: zoneId,
          response_code: status,
          ready_state: nextSource.readyState,
          retry_count: reconnectAttempt,
        });

        scheduleReconnect(
          status ? `http_${status}` : "network_error",
          false,
          status,
        );
      });
    }

    const reconnectNow = (reason: string) => {
      if (disposed || permissionDenied || sessionInvalid || refreshing) return;
      cancelReconnect();
      scheduleReconnect(reason, true);
    };
    const onOnline = () => reconnectNow("online");
    const onVisibility = () => {
      capture("page_visibility_changed", {
        zone_id: zoneId,
        state: document.visibilityState,
      });
      if (document.visibilityState === "visible") reconnectNow("foreground");
    };

    const stallTimer = window.setInterval(() => {
      if (
        document.visibilityState !== "visible" ||
        disposed ||
        permissionDenied ||
        sessionInvalid ||
        refreshing
      )
        return;
      const idleFor = Date.now() - lastActivityAt;
      if (idleFor < STALL_TIMEOUT_MS) return;
      capture("sse_stalled", {
        zone_id: zoneId,
        ready_state: source?.readyState,
        ms_since_activity: idleFor,
      });
      scheduleReconnect("stalled");
    }, STALL_CHECK_INTERVAL_MS);

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    connect();

    return () => {
      disposed = true;
      generation += 1;
      cancelReconnect();
      window.clearInterval(stallTimer);
      closeCurrent();
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    capture,
    getAccessToken,
    onPermissionDenied,
    onRecovered,
    onSessionInvalid,
    onScan,
    onUnavailable,
    refreshSession,
    url,
    zoneId,
  ]);
}
